import { BehaviorSubject } from 'rxjs';
import { parseLanguageToShortLanguage } from './languageParser.service';

export const DEFAULT_LANGUAGE = "nl";
export const DEFAULT_LANGUAGE_CHATBOT = "nl-NL";

// Interface of the translationService because we won't expose the translationServiceController because it has to have a non-public constructor.
export interface ITranslationService {
    translations$: BehaviorSubject<any>;
    language$: BehaviorSubject<string>;
    setLanguage(language: string): void;
}

class TranslationServiceController {
    private _translations: any;
    private _language = DEFAULT_LANGUAGE;

    public translations$: BehaviorSubject<any> = new BehaviorSubject(null);
    public language$: BehaviorSubject<string> = new BehaviorSubject(DEFAULT_LANGUAGE);

    constructor(private assetsBaseUrl: string, private filePrefix: string, private defaultLanguage: string) {
        if (!assetsBaseUrl) {
            throw new Error("Could not create new translationService without assetsBaseUrl");
        }
        controllers[filePrefix] = this;
        this._language = this.defaultLanguage;
        this.language$.next(this.defaultLanguage);
        this.getLocaleComponentStrings(this.assetsBaseUrl, this._language, filePrefix).then(translations => {
            this._translations = translations;
            this.translations$.next(this._translations)
        });
    }

    public setLanguage(language: string) {
        this.getLocaleComponentStrings(this.assetsBaseUrl, language, this.filePrefix).then(translations => {
            this._translations = translations;
            this._language = language;
            this.language$.next(language);
            this.translations$.next(this._translations)
        });
    }

    //Create promise and try to fetch the translations;
    private fetchLocaleStringsForComponent(
        baseUrl: string, fileName: string, locale: string,
    ): Promise<any> {
        return new Promise(
            (resolve, reject): void => {
                fetch(baseUrl + `i18n/${fileName}.i18n.${parseLanguageToShortLanguage(locale)}.json`).then(
                    result => result.ok
                        ? resolve(result.json())
                        : reject(),
                    () => reject()
                );
            }
        );
    }

    // Retrieve the translations for the specified language.
    private async getLocaleComponentStrings(
        baseUrl: string,
        language: string,
        fileName: string = "chatbot"
    ): Promise<any> {
        try {
            return await this.fetchLocaleStringsForComponent(
                baseUrl,
                fileName,
                language
            );
        } catch (e) {
            console.warn(
                `no locale for ${fileName} (${language}) loading default locale ${DEFAULT_LANGUAGE}.`
            );
            return await this.fetchLocaleStringsForComponent(baseUrl, fileName, DEFAULT_LANGUAGE);
        }
    }
}

const controllers = {}

// TranslationService is setUp as a Singleton per type.
// This gives the possibility to create a translationservice for the chatbot and a translationservice for the account part.
// Which will fetch their own translation files
// For each type, the translationService has to be initiated once with a baseUrl and a configurationPrefix (for example 'chatbot' or 'account').
// After the initialization every component can just get the service by calling TranslationService({type});
export const TranslationService = (configurationPrefix: string, defaultLanguage: string = DEFAULT_LANGUAGE, baseUrl = null): ITranslationService =>
    !!controllers[configurationPrefix] ?
        controllers[configurationPrefix] :
        new TranslationServiceController(baseUrl, configurationPrefix, defaultLanguage);
